import React, { Dispatch, memo, SetStateAction, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./SlideCommentCard.module.css";
import { Label14, TextPrimary } from "../../../../../common/components/typography/Typography";
import { ParentComponentProps } from "../../../../../common/ParentComponentProps";
import { stringifyVideoTimeAgo } from "../../../../../common/video/util/videoTimeStrings";
import { SlideComment } from "../../../../../models/SlideComment";
import { MoreVert } from "@material-ui/icons";
import PuiTypography from "../../../../../components/elements/PuiTypography";
import { DeleteSlideComment, UpdateSlideComment } from "../../../../../store/actions/SlideActions";
import {
    Button,
    createStyles,
    createTheme,
    makeStyles,
    Modal,
    TextField,
    Theme,
    ThemeProvider,
} from "@material-ui/core";
import { AppState } from "../../../../../root/RootReducer";
import { IsDevice } from "../../../../../components/function/useDeviceFlag";
import { t } from "i18next";

interface CommentProps {
    setPageNumber: (pageNumber: number) => void;
    comment: SlideComment;
    setIsUnderlKeyBorardControl: Dispatch<SetStateAction<boolean>>;
}

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        textField: {
            width: IsDevice("iphone") ? "310px" : "850px",
            minHeight: IsDevice("iphone") ? "none" : "130px",
            background: "#FFFFFF0D",
            border: "none",
            borderRadius: "5px",
            "& .MuiInputBase-root": {
                "& .MuiInputBase-input": {
                    color: "var(--white)",
                    fontFamily: "Yu Gothic",
                },
            },
        },
    })
);

function CommentCard({ setPageNumber, comment, setIsUnderlKeyBorardControl }: CommentProps) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [isUserInput, ChangeUserInputFlag] = useState(false);
    const [isShowMore, setIsShowMore] = useState<boolean>(false);
    const [isOpenUpdateCommentModal, setIsOpenUpdateCommentModal] = useState<boolean>(false);
    const [commentText, setCommentText] = useState<string>(comment.text);
    const [inputText, setInputText] = useState<string>(commentText);

    const { userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
    }));

    const handleUpdateComment = () => {
        setIsShowMore(false);
        setIsOpenUpdateCommentModal(true);
        setIsUnderlKeyBorardControl(false);
    };

    const handleDeleteComment = () => {
        if (!comment.id) return;
        dispatch(DeleteSlideComment(comment.id, comment.userId, comment.documentId));
        setIsShowMore(false);
    };

    const handleClick = () => {
        setPageNumber(comment.commentPages);
    };

    const InputCommentText = (event: any) => {
        ChangeUserInputFlag(true);
        setInputText(event.target.value);
    };

    const CloseUpdateCommentModal = () => {
        setIsOpenUpdateCommentModal(false);
        setIsUnderlKeyBorardControl(true);
    };

    const DoneUpdateComment = () => {
        dispatch(
            UpdateSlideComment({
                id: comment.id,
                userId: comment.userId,
                documentId: comment.documentId,
                text: inputText,
                commentPages: comment.commentPages,
                isPrivate: comment.isPrivate || false,
            })
        );
        setCommentText(inputText);
        setIsShowMore(false);
        setIsOpenUpdateCommentModal(false);
        setIsUnderlKeyBorardControl(true);
    };

    return (
        <div className={styles.commentCard}>
            <div className={styles.icon}>
                <img
                    src={comment.userImageUrl}
                    onError={(e) =>
                        (e.currentTarget.src =
                            require("../../../../../assets/image/noPresenterImage.svg").default)
                    }
                    alt=""
                />
            </div>

            <div className={styles.commentDetail}>
                <div className={styles.meta}>
                    <TextPrimary>{comment.userName}</TextPrimary>

                    <CommentMetaItem>
                        <Label14 color="gray-42">
                            {stringifyVideoTimeAgo(
                                typeof comment.created === "string"
                                    ? new Date(comment.created)
                                    : comment.created
                            )}
                        </Label14>
                    </CommentMetaItem>
                </div>

                <div
                    className={`${styles.body} ${isShowMore ? styles.isActive : ""}`}
                    onClick={handleClick}
                >
                    <div className={styles.commentPages}> p.{comment.commentPages}</div>
                    <TextPrimary>{commentText}</TextPrimary>
                </div>
            </div>

            {userInfo && Number(userInfo.id) === comment.userId && (
                <div className={styles.moreArea}>
                    <div onClick={() => setIsShowMore(!isShowMore)} className={styles.moreIcon}>
                        <MoreVert />
                    </div>
                    {isShowMore && (
                        <>
                            <div className={styles.menuItem}>
                                <div onClick={() => handleUpdateComment()}>
                                    <PuiTypography>{t("コメントを変更")}</PuiTypography>
                                </div>
                                <div onClick={() => handleDeleteComment()}>
                                    <PuiTypography> {t("削除")}</PuiTypography>
                                </div>
                            </div>
                            <div
                                className={styles.selectOtherArea}
                                onClick={() => setIsShowMore(false)}
                            />
                        </>
                    )}
                </div>
            )}

            {/* スキル選択モーダル */}
            <Modal open={isOpenUpdateCommentModal} onClose={CloseUpdateCommentModal}>
                <div
                    className={styles.updateCommentModal}
                    style={{
                        width: IsDevice("iphone") ? "360px" : IsDevice("ipad") ? "740px" : "900px",
                    }}
                >
                    <div>
                        <ThemeProvider theme={theme}>
                            <TextField
                                className={classes.textField}
                                color="secondary"
                                onChange={InputCommentText}
                                multiline
                                fullWidth
                                minRows={5}
                                autoComplete="off"
                                variant="outlined"
                                defaultValue={commentText}
                            />
                        </ThemeProvider>
                    </div>
                    <div className={styles.updateCommentButtonArea}>
                        <div className={styles.cancelButton}>
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={CloseUpdateCommentModal}
                                style={{
                                    marginRight: "16px",
                                    color: "var(--loading-bg)",
                                    fontFamily: "noto-sans-cjk-jp,sans-serif",
                                    border: "solid 2px #5D5D5D",
                                    width: IsDevice("iphone") ? "125px" : "200px",
                                    fontSize: "16px",
                                }}
                            >
                                {t("キャンセル")}
                            </Button>
                        </div>
                        <div className={styles.updateButton}>
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={DoneUpdateComment}
                                disabled={!isUserInput}
                                style={
                                    isUserInput
                                        ? {
                                              color: "var(--white)",
                                              fontWeight: "bold",
                                              fontFamily: "noto-sans-cjk-jp,sans-serif",
                                              backgroundColor: "var(--studio-btn-bg)",
                                              width: IsDevice("iphone") ? "125px" : "200px",
                                              fontSize: "16px",
                                          }
                                        : {
                                              color: "var(--gray-85)",
                                              borderColor: "var(--gray-85)",
                                              width: IsDevice("iphone") ? "125px" : "200px",
                                              fontSize: "16px",
                                          }
                                }
                            >
                                {t("更新")}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function CommentMetaItem({ children }: ParentComponentProps) {
    return <div className={styles.metaItem}>{children}</div>;
}

export default memo(CommentCard);
